import { db, type Session } from '../db';

export interface DailySummary {
  totalPumpMl: number;
  nursingCount: number;
  bottleCount: number;
  bottleVolumeMl: number;
  totalSessions: number;
}

export async function getDailySummary(childId: string, date: number): Promise<DailySummary> {
  const dayStart = new Date(date);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(date);
  dayEnd.setHours(23, 59, 59, 999);

  const sessions = await db.sessions
    .where('[childId+startTime]')
    .between([childId, dayStart.getTime()], [childId, dayEnd.getTime()])
    .toArray();

  const active = sessions.filter((s) => !s.deletedAt);

  return {
    totalPumpMl: active
      .filter((s) => s.type === 'pumping')
      .reduce((sum, s) => sum + (s.volumeMl || 0), 0),
    nursingCount: active.filter((s) => s.type === 'nursing').length,
    bottleCount: active.filter((s) => s.type === 'bottle').length,
    bottleVolumeMl: active
      .filter((s) => s.type === 'bottle')
      .reduce((sum, s) => sum + (s.volumeMl || 0), 0),
    totalSessions: active.length,
  };
}

export interface DailyOutput {
  date: number;
  totalPumpMl: number;
  sessionCount: number;
}

export async function getWeeklyOutput(childId: string, days: number = 7): Promise<DailyOutput[]> {
  const now = Date.now();
  const results: DailyOutput[] = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = now - i * 24 * 60 * 60 * 1000;
    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(date);
    dayEnd.setHours(23, 59, 59, 999);

    const sessions = await db.sessions
      .where('[childId+startTime]')
      .between([childId, dayStart.getTime()], [childId, dayEnd.getTime()])
      .toArray();

    const active = sessions.filter((s) => !s.deletedAt);
    results.push({
      date: dayStart.getTime(),
      totalPumpMl: active
        .filter((s) => s.type === 'pumping')
        .reduce((sum, s) => sum + (s.volumeMl || 0), 0),
      sessionCount: active.length,
    });
  }

  return results;
}

export async function getAverageSessionDuration(
  childId: string,
  days: number,
  type: Session['type'],
): Promise<number> {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const sessions = await db.sessions
    .where('[childId+startTime]')
    .between([childId, cutoff], [childId, Number.MAX_SAFE_INTEGER])
    .toArray();

  const filtered = sessions.filter((s) => !s.deletedAt && s.type === type && s.durationMs > 0);
  if (filtered.length === 0) return 0;
  return filtered.reduce((sum, s) => sum + s.durationMs, 0) / filtered.length;
}

export async function getFeedingFrequency(childId: string, days: number): Promise<number> {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const sessions = await db.sessions
    .where('[childId+startTime]')
    .between([childId, cutoff], [childId, Number.MAX_SAFE_INTEGER])
    .toArray();

  const filtered = sessions.filter((s) => !s.deletedAt);
  if (days === 0) return 0;
  return filtered.length / days;
}
